/**
 * Holiday Kits — one place to turn holidays on/off.
 *
 * Each kit bundles: season hero, special menu, banner/promo, items (prices +
 * order-by dates), images, and optional social captions. Nothing is deleted;
 * kits are toggled off or archived. Template-friendly labels (not bakery-specific).
 */

import { useEffect, useState } from 'react';
import {
  HiCalendar,
  HiDuplicate,
  HiEye,
  HiPlus,
  HiRefresh,
  HiSparkles,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SocialKit {
  captions?: string[];
  image_urls?: string[];
  notes?: string;
}

interface KitItem {
  id?: string;
  menu_item_id: string;
  is_included: boolean;
  sync_menu_availability: boolean;
  special_price: number | null;
  special_description: string | null;
  order_by: string | null;
  pickup_through: string | null;
  display_order: number;
  menu_item?: {
    id: string;
    name: string;
    price: number;
    image_url: string | null;
    is_available: boolean;
  };
}

interface HolidayKit {
  id: string;
  name: string;
  slug: string;
  holiday_key: string;
  year: number;
  description: string | null;
  is_enabled: boolean;
  is_archived: boolean;
  is_live_now?: boolean;
  start_date: string | null;
  end_date: string | null;
  creatives_ready_date: string | null;
  season_id: string | null;
  collection_id: string | null;
  promo_id: string | null;
  social_kit: SocialKit;
  item_count?: number;
  included_count?: number;
}

interface MenuOption {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
}

function dateInput(value: string | null | undefined) {
  if (!value) return '';
  return String(value).split('T')[0];
}

function statusLabel(kit: HolidayKit) {
  if (kit.is_archived) return { text: 'Archived', className: 'bg-gray-100 text-gray-600' };
  if (kit.is_live_now) return { text: 'Live now', className: 'bg-green-100 text-green-800' };
  if (kit.is_enabled) return { text: 'Scheduled', className: 'bg-amber-100 text-amber-800' };
  return { text: 'Off', className: 'bg-gray-100 text-gray-700' };
}

export default function Holidays() {
  const [kits, setKits] = useState<HolidayKit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [items, setItems] = useState<KitItem[]>([]);
  const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<Partial<HolidayKit>>({});
  const [socialNotes, setSocialNotes] = useState('');
  const [socialCaptions, setSocialCaptions] = useState('');

  async function loadKits() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${API_URL}/api/holiday-kits`);
      if (!res.ok) throw new Error('Could not load holidays');
      const data = await res.json();
      setKits(data.kits || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadKits();
    adminFetch(`${API_URL}/api/menu`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setMenuOptions(d.items || []))
      .catch(() => setMenuOptions([]));
  }, []);

  async function openKit(id: string) {
    setSelectedId(id);
    setPreview(null);
    const res = await adminFetch(`${API_URL}/api/holiday-kits/${id}`);
    if (!res.ok) {
      setError('Could not open holiday');
      return;
    }
    const data = await res.json();
    setForm(data.kit);
    setItems(data.items || []);
    const sk = data.kit?.social_kit || {};
    setSocialNotes(sk.notes || '');
    setSocialCaptions((sk.captions || []).join('\n'));
  }

  async function toggleKit(kit: HolidayKit) {
    const res = await adminFetch(`${API_URL}/api/holiday-kits/${kit.id}/toggle`, {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ is_enabled: !kit.is_enabled }),
    });
    if (!res.ok) {
      alert('Could not flip the switch. Try again.');
      return;
    }
    await loadKits();
    if (selectedId === kit.id) await openKit(kit.id);
  }

  async function saveKit() {
    if (!selectedId) return;
    setSaving(true);
    try {
      const social_kit = {
        captions: socialCaptions
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        image_urls: form.social_kit?.image_urls || [],
        notes: socialNotes.trim(),
      };
      const res = await adminFetch(`${API_URL}/api/holiday-kits/${selectedId}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          creatives_ready_date: form.creatives_ready_date || null,
          is_archived: form.is_archived,
          social_kit,
        }),
      });
      if (!res.ok) throw new Error('Save failed');

      const itemsRes = await adminFetch(`${API_URL}/api/holiday-kits/${selectedId}/items`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          items: items.map((i, idx) => ({
            menu_item_id: i.menu_item_id,
            is_included: i.is_included,
            sync_menu_availability: i.sync_menu_availability,
            special_price: i.special_price,
            special_description: i.special_description,
            order_by: i.order_by,
            pickup_through: i.pickup_through,
            display_order: idx + 1,
          })),
        }),
      });
      if (!itemsRes.ok) throw new Error('Items save failed');
      await loadKits();
      await openKit(selectedId);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function duplicateNextYear() {
    if (!selectedId) return;
    if (!confirm('Make a copy for next year? The current holiday stays as-is (turned off on the copy).')) return;
    const res = await adminFetch(`${API_URL}/api/holiday-kits/${selectedId}/duplicate`, {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ years: 1 }),
    });
    if (!res.ok) {
      alert('Could not duplicate');
      return;
    }
    const data = await res.json();
    await loadKits();
    if (data.kit?.id) await openKit(data.kit.id);
  }

  async function rollDates() {
    if (!selectedId) return;
    if (!confirm('Move all dates forward one year on THIS kit? It will turn Off so you can review before going live.')) return;
    const res = await adminFetch(`${API_URL}/api/holiday-kits/${selectedId}/roll-dates`, {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ years: 1 }),
    });
    if (!res.ok) {
      alert('Could not roll dates');
      return;
    }
    await loadKits();
    await openKit(selectedId);
  }

  async function loadPreview() {
    if (!selectedId) return;
    const res = await adminFetch(`${API_URL}/api/holiday-kits/${selectedId}/preview`);
    if (!res.ok) {
      alert('Preview failed');
      return;
    }
    const data = await res.json();
    setPreview(data.preview);
  }

  function addMenuItem(menuId: string) {
    if (!menuId || items.some((i) => i.menu_item_id === menuId)) return;
    const m = menuOptions.find((x) => x.id === menuId);
    if (!m) return;
    setItems((prev) => [
      ...prev,
      {
        menu_item_id: m.id,
        is_included: true,
        sync_menu_availability: true,
        special_price: m.price,
        special_description: null,
        order_by: form.end_date || null,
        pickup_through: form.end_date || null,
        display_order: prev.length + 1,
        menu_item: {
          id: m.id,
          name: m.name,
          price: m.price,
          image_url: m.image_url,
          is_available: true,
        },
      },
    ]);
  }

  async function createBlankKit() {
    const name = prompt('Holiday name (e.g. Valentine\'s 2027)');
    if (!name?.trim()) return;
    const key = prompt('Short key (e.g. valentines)', 'holiday') || 'holiday';
    const year = parseInt(prompt('Year', String(new Date().getFullYear())) || '', 10);
    const res = await adminFetch(`${API_URL}/api/holiday-kits`, {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        name: name.trim(),
        holiday_key: key,
        year: year || new Date().getFullYear(),
      }),
    });
    if (!res.ok) {
      alert('Could not create holiday');
      return;
    }
    const data = await res.json();
    await loadKits();
    if (data.kit?.id) await openKit(data.kit.id);
  }

  if (loading) {
    return <div className="p-6 text-gray-600">Loading holidays…</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiSparkles className="text-amber-500" />
            Holidays
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            Turn a whole holiday on or off with one switch. Each holiday holds its homepage hero,
            special menu page, banner, items, and dates.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => loadKits()}
            className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-1"
          >
            <HiRefresh /> Refresh
          </button>
          <button
            type="button"
            onClick={createBlankKit}
            className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1"
          >
            <HiPlus /> New holiday
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-900">
        <strong>Tip:</strong> Flip the switch to ON when you are ready. Use start/end dates so it
        only shows in that window. Use Preview before going live. Nothing is deleted — use Archive
        to hide old years from this list.
      </div>

      <div className="space-y-3">
        {kits
          .filter((k) => !k.is_archived || selectedId === k.id)
          .map((kit) => {
            const st = statusLabel(kit);
            return (
              <div
                key={kit.id}
                className={`border rounded-xl bg-white shadow-sm ${
                  selectedId === kit.id ? 'ring-2 ring-purple-300' : ''
                }`}
              >
                <div className="p-4 flex flex-wrap items-center gap-3 justify-between">
                  <button
                    type="button"
                    className="text-left flex-1 min-w-[200px]"
                    onClick={() => openKit(kit.id)}
                  >
                    <div className="font-semibold text-gray-900">{kit.name}</div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full ${st.className}`}>{st.text}</span>
                      <span className="flex items-center gap-1">
                        <HiCalendar /> {dateInput(kit.start_date) || '—'} → {dateInput(kit.end_date) || '—'}
                      </span>
                      <span>
                        {kit.included_count ?? 0}/{kit.item_count ?? 0} items
                      </span>
                      {kit.creatives_ready_date && (
                        <span>Creatives from {dateInput(kit.creatives_ready_date)}</span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleKit(kit)}
                    className={`relative w-16 h-8 rounded-full transition-colors ${
                      kit.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    aria-label={kit.is_enabled ? 'Turn off' : 'Turn on'}
                  >
                    <span
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        kit.is_enabled ? 'left-9' : 'left-1'
                      }`}
                    />
                    <span className={`absolute text-[10px] font-bold top-2 ${
                      kit.is_enabled ? 'left-2 text-white' : 'right-2 text-gray-600'
                    }`}>
                      {kit.is_enabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        {kits.length === 0 && (
          <p className="text-gray-500 text-sm">No holidays yet. Create one or run the seed apply.</p>
        )}
      </div>

      {selectedId && form.id && (
        <div className="mt-6 border rounded-xl bg-white p-4 sm:p-6 space-y-5 shadow-sm">
          <div className="flex flex-wrap justify-between gap-2 items-center">
            <h2 className="text-lg font-semibold text-gray-900">Edit {form.name}</h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadPreview}
                className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 hover:bg-gray-50"
              >
                <HiEye /> Preview
              </button>
              <button
                type="button"
                onClick={duplicateNextYear}
                className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 hover:bg-gray-50"
              >
                <HiDuplicate /> Duplicate next year
              </button>
              <button
                type="button"
                onClick={rollDates}
                className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Roll dates +1 year
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="font-medium text-gray-700">Name</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              <span className="font-medium text-gray-700">Page path</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={`/menu/${form.slug || ''}`}
                readOnly
              />
            </label>
            <label className="text-sm">
              <span className="font-medium text-gray-700">Start date (go live)</span>
              <input
                type="date"
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={dateInput(form.start_date)}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </label>
            <label className="text-sm">
              <span className="font-medium text-gray-700">End date</span>
              <input
                type="date"
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={dateInput(form.end_date)}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Creatives ready (optional)</span>
              <input
                type="date"
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={dateInput(form.creatives_ready_date)}
                onChange={(e) => setForm({ ...form, creatives_ready_date: e.target.value })}
              />
              <span className="text-xs text-gray-500">Reminder only — does not show on the website.</span>
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Notes for your team</span>
              <textarea
                className="mt-1 w-full border rounded-lg px-3 py-2"
                rows={2}
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
          </div>

          {/* Items */}
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Items on this holiday</h3>
            <p className="text-xs text-gray-500 mb-2">
              Untick “Show” to pull one item without a sold-out label. Year-round items (like pecan pie)
              can leave “Hide when off” unchecked.
            </p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-2">Show</th>
                    <th className="p-2">Item</th>
                    <th className="p-2">Price</th>
                    <th className="p-2">Order by</th>
                    <th className="p-2">Pickup through</th>
                    <th className="p-2">Hide when off</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.menu_item_id} className="border-t">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={item.is_included}
                          onChange={(e) => {
                            const next = [...items];
                            next[idx] = { ...item, is_included: e.target.checked };
                            setItems(next);
                          }}
                        />
                      </td>
                      <td className="p-2 font-medium">{item.menu_item?.name || item.menu_item_id}</td>
                      <td className="p-2">
                        <input
                          type="number"
                          step="0.01"
                          className="w-20 border rounded px-1 py-0.5"
                          value={item.special_price ?? ''}
                          onChange={(e) => {
                            const next = [...items];
                            next[idx] = {
                              ...item,
                              special_price: e.target.value === '' ? null : Number(e.target.value),
                            };
                            setItems(next);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="date"
                          className="border rounded px-1 py-0.5"
                          value={dateInput(item.order_by)}
                          onChange={(e) => {
                            const next = [...items];
                            next[idx] = { ...item, order_by: e.target.value || null };
                            setItems(next);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="date"
                          className="border rounded px-1 py-0.5"
                          value={dateInput(item.pickup_through)}
                          onChange={(e) => {
                            const next = [...items];
                            next[idx] = { ...item, pickup_through: e.target.value || null };
                            setItems(next);
                          }}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={item.sync_menu_availability}
                          title="When checked, turning the holiday off also hides this item on the regular menu"
                          onChange={(e) => {
                            const next = [...items];
                            next[idx] = { ...item, sync_menu_availability: e.target.checked };
                            setItems(next);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-3 text-gray-500">
                        No items yet — add from your menu below.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <select
                className="border rounded-lg px-2 py-1.5 text-sm max-w-full"
                defaultValue=""
                onChange={(e) => {
                  addMenuItem(e.target.value);
                  e.target.value = '';
                }}
              >
                <option value="">Add menu item…</option>
                {menuOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} (${m.price})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Social kit — storage only */}
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Social kit (storage only)</h3>
            <p className="text-xs text-gray-500 mb-2">
              Save approved captions for later. Nothing posts automatically.
            </p>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              rows={3}
              placeholder="One caption per line"
              value={socialCaptions}
              onChange={(e) => setSocialCaptions(e.target.value)}
            />
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={2}
              placeholder="Notes (which Canva file, who approved, etc.)"
              value={socialNotes}
              onChange={(e) => setSocialNotes(e.target.value)}
            />
          </div>

          {preview && (
            <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-sm">
              <div className="font-medium mb-1">Preview</div>
              <pre className="whitespace-pre-wrap text-xs overflow-x-auto">
                {JSON.stringify(preview, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <label className="flex items-center gap-2 text-sm text-gray-600 mr-auto">
              <input
                type="checkbox"
                checked={Boolean(form.is_archived)}
                onChange={(e) => setForm({ ...form, is_archived: e.target.checked })}
              />
              Archive (hide from main list)
            </label>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="px-4 py-2 text-sm border rounded-lg"
            >
              Close
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={saveKit}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save holiday'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
